// NOTICE: KAPTION-INTEGRATION

const CARD_DEFINITIONS = {
  action: {
    requiredButtons: ['action'],
    allowedStates: ['pending', 'completed'],
    resolutionActions: {
      action: { state: 'completed', buttonKey: 'action' },
    },
  },
  confirmation: {
    requiredButtons: ['confirm', 'cancel'],
    allowedStates: ['pending', 'confirmed', 'cancelled'],
    resolutionActions: {
      confirm: { state: 'confirmed', buttonKey: 'confirm' },
      cancel: { state: 'cancelled', buttonKey: 'cancel' },
    },
  },
};

const isValidIsoTimestamp = value => {
  if (!value) return false;
  const parsed = new Date(value);
  return (
    !Number.isNaN(parsed.getTime()) &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value)
  );
};

/**
 * Simple YAML parser for note card format.
 * Handles nested YAML up to arbitrary depth using indentation.
 * Does NOT handle anchors, multi-doc, flow sequences, or complex YAML features.
 */
const parseYaml = content => {
  const lines = content.replace(/\r\n?/g, '\n').split('\n');

  const startIdx = lines.findIndex(l => l.trim() === '---');
  if (startIdx < 0) return null;

  const result = {};
  // Stack tracks nested objects: [{ obj, indent }]
  const stack = [{ obj: result, indent: -1 }];

  for (let i = startIdx + 1; i < lines.length; i += 1) {
    const line = lines[i];

    if (line.trim() === '') {
      // eslint-disable-next-line no-continue
      continue;
    }
    if (line.trim() === '---' || line.trim() === '...') break;

    const separatorIndex = line.indexOf(':');
    if (separatorIndex === -1) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // Calculate indentation (number of leading spaces)
    const indent = line.search(/\S/);
    const key = line.slice(0, separatorIndex).trim();
    let value = line.slice(separatorIndex + 1).trim();

    // Handle quoted strings
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }

    // Pop stack back to parent of current indent level
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop();
    }

    const parent = stack[stack.length - 1].obj;

    if (value === '') {
      // This key may become a nested object — set empty string for now
      // If next line is more indented, it'll become an object
      const nextNonEmpty = lines.slice(i + 1).find(l => l.trim() !== '');
      const nextIndent = nextNonEmpty ? nextNonEmpty.search(/\S/) : 0;

      if (nextIndent > indent) {
        const nested = {};
        parent[key] = nested;
        stack.push({ obj: nested, indent });
      } else {
        parent[key] = value;
      }
    } else {
      parent[key] = value;
    }
  }

  return result;
};

const needsQuoting = str =>
  /[#{}[\]&*|>!%@`]/.test(str) ||
  /^['":?,\s-]/.test(str) ||
  /^\s|\s$/.test(str);

const quoteValue = str => {
  if (str === '') return "''";
  if (!needsQuoting(str)) return str;
  return `"${str.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
};

const serializeYaml = obj => {
  const lines = ['---'];

  const writeObj = (data, indent = '') => {
    if (!data || typeof data !== 'object') return;

    Object.entries(data).forEach(([key, value]) => {
      if (
        value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
      ) {
        lines.push(`${indent}${key}:`);
        writeObj(value, indent + '  ');
      } else {
        const strVal = value == null ? '' : String(value);
        lines.push(`${indent}${key}: ${quoteValue(strVal)}`);
      }
    });
  };

  writeObj(obj);
  return lines.join('\n');
};

export const parseKaptionNoteCard = content => {
  if (typeof content !== 'string') return null;

  const trimmed = content.trim();
  if (!trimmed.startsWith('---')) return null;

  const yaml = parseYaml(trimmed);
  if (!yaml) return null;
  if (yaml.kaption_note_card !== 'v2') return null;

  const type = yaml.type;
  const definition = CARD_DEFINITIONS[type];
  if (!definition) return null;

  const buttons = yaml.buttons;
  if (!buttons || typeof buttons !== 'object') return null;
  if (definition.requiredButtons.some(key => !buttons[key])) return null;

  const state = yaml.state;
  if (!definition.allowedStates.includes(state)) return null;

  const resolution =
    typeof yaml.resolution === 'object' && yaml.resolution !== null
      ? yaml.resolution
      : {};

  if (state === 'pending') {
    if (resolution.selected) return null;
  } else {
    const selected = resolution.selected || '';
    const actionDef = definition.resolutionActions[selected];
    if (!actionDef || actionDef.state !== state) return null;
    if (!resolution.label || !resolution.by || !resolution.at) return null;
    if (!isValidIsoTimestamp(resolution.at)) return null;
  }

  const meta =
    typeof yaml.meta === 'object' && yaml.meta !== null ? yaml.meta : {};
  const data =
    typeof yaml.data === 'object' && yaml.data !== null ? yaml.data : {};
  const callback =
    typeof meta.callback === 'object' && meta.callback !== null
      ? meta.callback
      : {};

  return {
    type,
    title: yaml.title || '',
    subtitle: yaml.subtitle || '',
    buttons,
    state,
    meta: {
      ...meta,
      callback,
    },
    data,
    resolution: {
      selected: resolution.selected || '',
      label: resolution.label || '',
      by: resolution.by || '',
      at: resolution.at || '',
    },
  };
};

export const serializeKaptionNoteCard = noteCard => {
  const definition = CARD_DEFINITIONS[noteCard?.type];
  if (!definition) {
    throw new Error(`Unsupported Kaption note card type: ${noteCard?.type}`);
  }

  const obj = {
    kaption_note_card: 'v2',
    type: noteCard.type,
    title: noteCard.title || '',
    subtitle: noteCard.subtitle || '',
    buttons: noteCard.buttons || {},
    state: noteCard.state || '',
    meta: noteCard.meta || {},
    data: noteCard.data || {},
    resolution: noteCard.resolution || {
      selected: '',
      label: '',
      by: '',
      at: '',
    },
  };

  return serializeYaml(obj);
};

export const getKaptionNoteCardPreviewText = content => {
  const noteCard = parseKaptionNoteCard(content);
  if (!noteCard) return null;

  if (noteCard.type === 'action') {
    if (noteCard.state === 'completed') {
      return `Completed: ${noteCard.resolution.label || noteCard.buttons.action}`;
    }
    return `Action note: ${noteCard.title}`;
  }

  if (noteCard.state === 'confirmed') {
    return `Confirmed: ${noteCard.resolution.label || noteCard.buttons.confirm}`;
  }

  if (noteCard.state === 'cancelled') {
    return `Cancelled: ${noteCard.resolution.label || noteCard.buttons.cancel}`;
  }

  return `Confirmation note: ${noteCard.title}`;
};

export const formatKaptionNoteCardTimestamp = timestamp => {
  if (!isValidIsoTimestamp(timestamp)) return '';

  const [date, timeWithZone] = timestamp.split('T');
  return `${date} ${timeWithZone.slice(0, 5)} UTC`;
};

export { CARD_DEFINITIONS };
